'use client';
import React, { useEffect, useState } from 'react';
import { useAppStore } from '@/store/app-store';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { useForm, Controller } from 'react-hook-form';
import { Breadcrumb, Button, TextareaAutosize, Input, GlobalLoading, Alert } from '@/shared/components';
import { useMutationSubmitRequest } from '@/hooks/request-form.hook';
import { RequestForm } from '@/types/request';
import Cookies from 'js-cookie';
import { useProductDetailStore } from '@/app/product/[catalogNumber]/store';
import ClickAwayListener from '@mui/base/ClickAwayListener';
import { useQueryChildCatalogs } from '@/hooks/search-result.hook';
import { useGetProductByCatalogId } from '@/hooks/quick-order.hook';
import { usePageTracking, useEventTracking } from '@/shared/tracking/use-tracking';
import { trimAll } from '@/app/utils';
import { REGEX } from '@/app/utils/constant';

export default function RequestForm() {
  usePageTracking();
  const { trackEvent } = useEventTracking();
  const { t } = useLang(NAMESPACE.requestForm);
  const { userInfo, isAnonymous } = useAppStore();
  const requestType = Cookies.get('request_type');
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>('success');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [catalogNumber, setCatalogNumber] = useState<string>('');
  const [firstClick, setFirstClick] = useState<boolean>(true);
  const productDetailStore = useProductDetailStore();
  const submitRequest = useMutationSubmitRequest();
  const getProductByCatalogId = useGetProductByCatalogId();
  const { data: childCatalogs, isFetching: childCatalogsFetching } = useQueryChildCatalogs(catalogNumber?.trim());
  const {
    control,
    formState: { errors },
    getValues,
    reset,
    setValue,
    handleSubmit,
    watch,
    clearErrors,
  } = useForm<RequestForm>({
    mode: 'all',
    defaultValues: {
      requestDetails: {
        catalogNumber: '',
        productName: '',
        lotNumber: '',
        additionalComments: '',
      },
      contactInformation: {
        firstName: '',
        lastName: '',
        companyName: '',
        emailAddress: '',
        mobileNumber: '',
      },
      requestType: '',
    },
  });
  const formValues = watch();
  useEffect(() => {
    const data = trimAll(getValues());
    if (
      selectedOptions.length === 0 ||
      !data.requestDetails.catalogNumber ||
      !data.requestDetails.productName ||
      (!data.requestDetails.lotNumber && checkRequestType('COA')) ||
      !data.contactInformation.firstName ||
      !data.contactInformation.lastName ||
      !data.contactInformation.companyName ||
      !data.contactInformation.emailAddress ||
      !data.contactInformation.mobileNumber
    ) {
      setSubmitDisabled(true);
      return;
    }
    setSubmitDisabled(false);
  }, [formValues]);
  useEffect(() => {
    if (childCatalogsFetching) {
      GlobalLoading.show();
    } else {
      GlobalLoading.hide();
    }
  }, [childCatalogsFetching]);
  useEffect(() => {
    setValue('requestDetails.catalogNumber', catalogNumber);
    if (childCatalogs?.length > 0) {
      const childCatalogNumbers = childCatalogs.map((item) => item.childCatalogNumber);
      GlobalLoading.show();
      getProductByCatalogId.mutateAsync(childCatalogNumbers[0]).then((response) => {
        GlobalLoading.hide();
        if (response.code === '200') {
          setValue('requestDetails.productName', response?.data[0]?.productTitle);
        }
      });
    } else if (!childCatalogsFetching) {
      GlobalLoading.hide();
      setValue('requestDetails.productName', '');
    }
  }, [childCatalogs]);
  useEffect(() => {
    if (!isAnonymous) {
      setValue('contactInformation.firstName', userInfo?.firstName);
      setValue('contactInformation.lastName', userInfo?.lastName);
      setValue('contactInformation.emailAddress', userInfo?.emailAddress);
      setValue('contactInformation.companyName', userInfo?.company);
      setValue('contactInformation.mobileNumber', userInfo?.phoneNumber);
    }
    if (productDetailStore?.productDetail?.productBaseInfo?.catalogNumber) {
      setValue('requestDetails.catalogNumber', productDetailStore?.productDetail?.productBaseInfo?.catalogNumber);
      if (productDetailStore?.productDetail?.productBaseInfo?.productTitle) {
        setValue('requestDetails.productName', productDetailStore?.productDetail?.productBaseInfo?.productTitle);
      } else {
        setCatalogNumber(productDetailStore?.productDetail?.productBaseInfo?.catalogNumber);
      }
    }
  }, [userInfo]);
  useEffect(() => {
    if (requestType !== undefined) {
      setSelectedOptions([requestType.toUpperCase()]);
      Cookies.remove('request_type');
    }
  }, [requestType]);
  useEffect(() => {
    setValue('requestType', selectedOptions.toString());
  }, [selectedOptions]);

  useEffect(() => {
    clearErrors('requestDetails.lotNumber');
  }, [selectedOptions]);

  const requestTypes = [
    { label: t('msds'), value: 'MSDS' },
    {
      label: t('coa'),
      value: 'COA',
    },
    { label: t('technical.support'), value: 'Technical Support' },
    { label: t('others'), value: 'Others (General Inquiries)' },
  ];
  const handleReturnTypeClick = (option: string) => {
    if (selectedOptions.includes(option)) {
      setSelectedOptions(selectedOptions.filter((item) => item !== option));
    } else {
      setSelectedOptions([...selectedOptions, option]);
    }
  };
  const checkRequestType = (type) => {
    return selectedOptions.includes(type);
  };
  function getProperty(obj, path) {
    if (obj) {
      const properties = path.split('.');
      const currentProperty = properties.shift();
      if (properties.length === 0) {
        return obj[currentProperty];
      }

      return getProperty(obj[currentProperty], properties.join('.'));
    }
  }
  const handleRegistration = (formData) => {
    const data = trimAll(formData);
    GlobalLoading.show();
    submitRequest.mutateAsync(data).then((response: any) => {
      GlobalLoading.hide();
      setShowAlert(true);
      if (response.code === '200' && response.message === 'success') {
        setAlertType('success');
        trackEvent({ action: 'formCompletions' });
        handleReset();
      } else {
        setAlertType('error');
      }
    });
  };
  const handleReset = () => {
    reset();
    setSelectedOptions([]);
  };
  const handleClickAway = () => {
    setShowAlert(false);
  };
  const handleSearchEnterKey = (e) => {
    e.key === 'Enter' && setCatalogNumber(e.target.value);
  };
  const handleNumericInputChange = (e) => {
    if (!/[0-9a-zA-Z]/.test(e.key)) {
      e.preventDefault();
    }
  };
  const formClick = (e) => {
    if (firstClick) {
      trackEvent({ action: 'formStarts' });
      setFirstClick(false);
    }
  };

  const inputRequiredRules = (message) => {
    return {
      validateWhitespace: (value) => {
        if (value.trim() === '') {
          return message;
        }
        return true;
      },
    };
  };

  const validateLotNumber = (message) => {
    return {
      isRequired: (value) => {
        if (checkRequestType('COA') && value.trim() === '') {
          return message;
        }
        return true;
      },
    };
  };

  return (
    <div className="grid grid-cols-[1fr_345px] w-full">
      <div className="col-span-2 mt-8 w-full mb-5">
        <Breadcrumb items={[{ name: t('home'), url: '/' }, { name: 'Request Form' }]} />
      </div>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div>
          <Alert
            open={showAlert}
            onClose={() => setShowAlert(false)}
            type={alertType || 'success'}
            white
            className="w-full self-start mb-4 sm:min-w-[300px]"
            title={alertType === 'success' ? t('successful') : t('error')}
            content={alertType === 'success' ? t('success.msg') : t('error.msg')}
          />
        </div>
      </ClickAwayListener>
      <div className="col-span-2 grid">
        <form onClick={formClick}>
          <div className="flex border-0 border-b border-solid border-gray-300 py-7 sm:block md:block">
            <div className="w-[150px] font-medium text-base text-end mr-[100px] sm:text-justify md:text-justify">
              {t('request.type')}
              <span className="text-[#e71316]">*</span>
            </div>
            <div className="flex sm:pt-2 md:pt-2 flex-wrap">
              {requestTypes.map((type, index) => {
                const isSelected = selectedOptions.includes(type.value);
                return (
                  <div
                    key={index}
                    onClick={() => handleReturnTypeClick(type.value)}
                    className={`cursor-pointer mb-2 mr-5 border px-2.5 py-[5px] border-solid border-[#0092F3] ${
                      isSelected ? 'bg-[#bedaee] font-semibold' : ''
                    }`}
                  >
                    {type.label}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex border-0 border-b border-solid border-gray-300 py-7 sm:block md:block">
            <div className="w-[150px] font-medium text-base text-end mr-[100px] sm:text-justify md:text-justify">
              {t('request.details')}
            </div>
            <div className="grid grid-cols-[1fr_1fr_360px] sm:flex sm:flex-col md:flex md:flex-col gap-x-6 gap-y-6">
              <Controller
                name="requestDetails.catalogNumber"
                control={control}
                rules={{ validate: inputRequiredRules(t('max.16.char')) }}
                render={({ field }) => (
                  <Input
                    validation={{ message: getProperty(errors, 'requestDetails.catalogNumber')?.message }}
                    maxLength={16}
                    className="w-full"
                    label={t('catalog.number')}
                    required={true}
                    onKeyDown={handleSearchEnterKey}
                    {...field}
                    onBlur={(e) => {
                      field.onBlur();
                      setCatalogNumber(e.target.value);
                    }}
                  />
                )}
              />
              <Controller
                name="requestDetails.productName"
                control={control}
                rules={{ validate: inputRequiredRules(t('request.field')) }}
                render={({ field }) => (
                  <Input
                    validation={{ message: getProperty(errors, 'requestDetails.productName')?.message }}
                    className="w-full"
                    value={field.value}
                    label={t('product.name')}
                    required={true}
                    {...field}
                  />
                )}
              />
              <Controller
                name="requestDetails.lotNumber"
                control={control}
                rules={{
                  validate: validateLotNumber(t('request.field')),
                }}
                render={({ field }) => (
                  <Input
                    validation={{ message: getProperty(errors, 'requestDetails.lotNumber')?.message }}
                    maxLength={16}
                    className="w-full"
                    label={t('lot.number')}
                    required={checkRequestType('COA') ? true : false}
                    {...field}
                  />
                )}
              />
              <Controller
                name="requestDetails.additionalComments"
                control={control}
                render={({ field }) => (
                  <TextareaAutosize
                    label={t('additional.comments')}
                    className="col-span-3 w-full h-[150px]"
                    onChange={() => {}}
                    minRows={6}
                    {...field}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex py-7 sm:block md:block">
            <div className="w-[150px] font-medium text-base text-end mr-[100px] sm:text-justify md:text-justify">
              {t('contact.information')}
            </div>
            <div className="grid grid-cols-[1fr_1fr_360px] gap-x-6 gap-y-6 sm:flex sm:flex-col md:flex md:flex-col">
              <Controller
                name="contactInformation.lastName"
                control={control}
                rules={{ validate: inputRequiredRules(t('max.16.char')) }}
                render={({ field }) => (
                  <Input
                    validation={{ message: getProperty(errors, 'contactInformation.lastName')?.message }}
                    maxLength={16}
                    className="w-full"
                    label={t('last.name')}
                    required
                    //readonly={!isAnonymous}
                    {...field}
                  />
                )}
              />
              <Controller
                name="contactInformation.firstName"
                control={control}
                rules={{ validate: inputRequiredRules(t('max.16.char')) }}
                render={({ field }) => (
                  <Input
                    validation={{ message: getProperty(errors, 'contactInformation.firstName')?.message }}
                    maxLength={16}
                    className="w-full"
                    label={t('first.name')}
                    required
                    //readonly={!isAnonymous}
                    {...field}
                  />
                )}
              />
              <Controller
                name="contactInformation.companyName"
                control={control}
                rules={{ validate: inputRequiredRules(t('max.16.char')) }}
                render={({ field }) => (
                  <Input
                    validation={{ message: getProperty(errors, 'contactInformation.companyName')?.message }}
                    maxLength={16}
                    className="w-full"
                    label={t('company.name')}
                    //readonly={!isAnonymous}
                    required
                    {...field}
                  />
                )}
              />
              <Controller
                name="contactInformation.emailAddress"
                control={control}
                rules={{
                  pattern: {
                    value: REGEX.EMAIL,
                    message: t('email.invalid'),
                  },
                  validate: inputRequiredRules(t('request.field')),
                }}
                render={({ field }) => (
                  <Input
                    validation={{ message: getProperty(errors, 'contactInformation.emailAddress')?.message }}
                    className="w-full"
                    label={t('email.address')}
                    //readonly={!isAnonymous}
                    required
                    {...field}
                  />
                )}
              />
              <Controller
                name="contactInformation.mobileNumber"
                control={control}
                rules={{ validate: inputRequiredRules(t('request.field')) }}
                render={({ field }) => (
                  <Input
                    validation={{ message: getProperty(errors, 'contactInformation.mobileNumber')?.message }}
                    maxLength={16}
                    className="w-full"
                    type="text"
                    label={t('mobile.number')}
                    onKeyDown={handleNumericInputChange}
                    required
                    {...field}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex">
            <div className="w-[150px] font-medium text-base text-end mr-[100px] sm:hidden md:hidden"></div>
            <Button disabled={submitDisabled} onClick={handleSubmit(handleRegistration)}>
              {t('submit')}
            </Button>
            <Button type="outline" onClick={handleReset} className="ml-5">
              {t('clear.all')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
